function num(v) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Как в report__resource_statement / report__resource_remainder: round(..., 2). */
function roundQty(v) {
  return Math.round(num(v) * 100) / 100;
}

export const OFB_ROW_TYPE = {
  CONSTRUCTIVE: "constructive",
  MATERIAL: "material",
  NOMENCLATURE: "nomenclature",
};

/**
 * material_name из SQL может быть «id / name» — для отображения берём только наименование SBS.
 * material_name_1c — номенклатура 1С БК (поле с бэка).
 */
export function parseOperFactBkMaterialName(materialName) {
  const s = String(materialName || "").trim();
  if (!s) return { material_id: null, resource_name: "—" };
  const idx = s.indexOf(" / ");
  if (idx === -1) return { material_id: null, resource_name: s };
  const idPart = s.slice(0, idx).trim();
  const namePart = s.slice(idx + 3).trim();
  const materialId = /^\d+$/.test(idPart) ? Number(idPart) : idPart;
  return {
    material_id: materialId,
    resource_name: namePart || s,
  };
}

export function pickMaterialName1c(row) {
  return row?.material_name_1c ?? row?.naimenovanie ?? "—";
}

export function getOperFactBkRowMaterialId(row) {
  if (row?.material_id != null && row.material_id !== "") {
    const n = Number(row.material_id);
    return Number.isFinite(n) ? n : row.material_id;
  }
  const { material_id } = parseOperFactBkMaterialName(row?.material_name);
  return material_id;
}

/** Опции фильтра «Материал» из строк текущей выборки отчёта. */
export function buildOperFactBkMaterialFilterOptions(rows) {
  const base = [{ label: "Все материалы", value: null }];
  const seen = new Map();
  for (const row of rows || []) {
    const materialId = getOperFactBkRowMaterialId(row);
    if (materialId == null || materialId === "") continue;
    const key = String(materialId);
    if (seen.has(key)) continue;
    const { resource_name } = parseOperFactBkMaterialName(row?.material_name);
    seen.set(key, resource_name || key);
  }
  const items = [...seen.entries()]
    .sort((a, b) => String(a[1]).localeCompare(String(b[1]), "ru"))
    .map(([value, label]) => ({
      label: String(label),
      value: /^\d+$/.test(value) ? Number(value) : value,
    }));
  return base.concat(items);
}

export function filterOperFactBkRowsByMaterial(rows, materialId) {
  if (materialId == null || materialId === "") return rows || [];
  const key = String(materialId);
  return (rows || []).filter((row) => String(getOperFactBkRowMaterialId(row)) === key);
}

const zeroQty = () => ({
  plan_norm_qty: 0,
  fact_qty: 0,
  m19_qty: 0,
  spisanie_8110_qty: 0,
});

function sumLeafIntoNode(node, leaf) {
  node.plan_norm_qty = roundQty(node.plan_norm_qty + num(leaf.plan_norm_qty));
  node.fact_qty = roundQty(node.fact_qty + num(leaf.fact_qty));
  node.m19_qty = roundQty(node.m19_qty + num(leaf.m19_qty));
  node.spisanie_8110_qty = roundQty(
    node.spisanie_8110_qty + num(leaf.spisanie_8110_qty),
  );
}

/**
 * Дерево: constructive_name_1C → материал (ресурс + ед. изм.) → номенклатура 1С БК (лист).
 * Дубли по одной номенклатуре в одном материале суммируются.
 */
export function buildOperFactBkCompareTree(flatRows) {
  const rootChildren = [];
  const findChild = (arr, key, factory) => {
    let n = arr.find((c) => c._path === key);
    if (!n) {
      n = factory();
      arr.push(n);
    }
    return n;
  };

  const leafMap = new Map();

  for (const r of flatRows || []) {
    const constructive =
      r.constructive_name_1C != null && String(r.constructive_name_1C).trim() !== ""
        ? String(r.constructive_name_1C).trim()
        : "—";
    const { material_id, resource_name } = parseOperFactBkMaterialName(r.material_name);
    const material_name_1c = pickMaterialName1c(r);
    const matKey =
      material_id != null ? `m:${material_id}` : `n:${resource_name}`;
    const nomKey = `1c:${material_name_1c}`;

    const folderKey = `c:${constructive}`;
    const materialPath = `${folderKey}|${matKey}`;
    const leafKey = `${materialPath}|${nomKey}`;

    const folder = findChild(rootChildren, folderKey, () => ({
      _path: folderKey,
      _rowKey: folderKey,
      _depth: 0,
      _rowType: OFB_ROW_TYPE.CONSTRUCTIVE,
      _isLeaf: false,
      _label: constructive,
      constructive_name_1C: constructive,
      children: [],
      ...zeroQty(),
    }));

    const materialNode = findChild(folder.children, materialPath, () => ({
      _path: materialPath,
      _rowKey: materialPath,
      _depth: 1,
      _rowType: OFB_ROW_TYPE.MATERIAL,
      _isLeaf: false,
      _label: resource_name,
      constructive_name_1C: constructive,
      material_id,
      resource_name,
      material_name: r.material_name,
      unit_name: r.unit_name || "—",
      children: [],
      ...zeroQty(),
    }));

    let leaf = leafMap.get(leafKey);
    if (!leaf) {
      leaf = {
        _path: leafKey,
        _rowKey: leafKey,
        _depth: 2,
        _rowType: OFB_ROW_TYPE.NOMENCLATURE,
        _isLeaf: true,
        _label: material_name_1c,
        constructive_name_1C: constructive,
        material_id,
        resource_name,
        material_name_1c,
        unit_name: materialNode.unit_name,
        plan_norm_qty: 0,
        fact_qty: 0,
        m19_qty: 0,
        spisanie_8110_qty: 0,
        children: [],
      };
      leafMap.set(leafKey, leaf);
      materialNode.children.push(leaf);
    }

    leaf.plan_norm_qty = roundQty(leaf.plan_norm_qty + num(r.plan_norm_qty));
    leaf.fact_qty = roundQty(leaf.fact_qty + num(r.fact_qty));
    leaf.m19_qty = roundQty(leaf.m19_qty + num(r.m19_qty));
    leaf.spisanie_8110_qty = roundQty(
      leaf.spisanie_8110_qty + num(r.spisanie_8110_qty),
    );
  }

  const sumUp = (node) => {
    if (node._isLeaf) return;
    const z = zeroQty();
    node.plan_norm_qty = z.plan_norm_qty;
    node.fact_qty = z.fact_qty;
    node.m19_qty = z.m19_qty;
    node.spisanie_8110_qty = z.spisanie_8110_qty;
    for (const ch of node.children) {
      sumUp(ch);
      sumLeafIntoNode(node, ch);
    }
  };

  for (const n of rootChildren) sumUp(n);

  rootChildren.sort((a, b) =>
    String(a._label).localeCompare(String(b._label), "ru"),
  );
  for (const folder of rootChildren) {
    folder.children.sort((a, b) =>
      String(a.resource_name).localeCompare(String(b.resource_name), "ru"),
    );
    for (const mat of folder.children) {
      mat.children.sort((a, b) =>
        String(a.material_name_1c).localeCompare(String(b.material_name_1c), "ru"),
      );
    }
  }

  return rootChildren;
}

export function collectOperFactBkExpandablePaths(nodes, out = []) {
  for (const n of nodes || []) {
    if (!n._isLeaf) {
      out.push(n._path);
      collectOperFactBkExpandablePaths(n.children, out);
    }
  }
  return out;
}

/** Раскрыть все ветки дерева (конструктив → материал), чтобы были видны данные после фильтров. */
export function buildOperFactBkExpandedForTree(nodes) {
  const expanded = {};
  for (const path of collectOperFactBkExpandablePaths(nodes)) {
    expanded[path] = true;
  }
  return expanded;
}

/** Поля узла, по которым ищем (как в applyTreeSearch на экране). */
export function operFactBkNodeSearchHaystack(node) {
  return [
    node?._label,
    node?.material_name,
    node?.resource_name,
    node?.material_name_1c,
    node?.constructive_name_1C,
    node?.unit_name,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ");
}

export function operFactBkNodeMatchesSearch(node, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return false;
  return operFactBkNodeSearchHaystack(node).toLowerCase().includes(q);
}

/** Узел или любой потомок подходит под запрос — показываем ветку в таблице. */
export function operFactBkBranchHasSearchMatch(node, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  if (operFactBkNodeMatchesSearch(node, q)) return true;
  return (node?.children || []).some((ch) =>
    operFactBkBranchHasSearchMatch(ch, q),
  );
}

/** Раскрыть предков (и папку с совпадением), чтобы найденная строка была видна. */
export function operFactBkExpandPathsForNode(node) {
  const paths = {};
  if (!node?._path) return paths;

  const parts = node._path.split("|").filter(Boolean);
  for (let i = 1; i < parts.length; i++) {
    paths[parts.slice(0, i).join("|")] = true;
  }
  if (node.children?.length) {
    paths[node._path] = true;
  }
  return paths;
}
